import {
  VUE_HTML_CHAMPS_MAX,
  VUE_HTML_LIENS_MAX,
  VUE_HTML_PREFIXE,
  VUE_HTML_PROTOCOLE,
  VUE_HTML_TAILLE_MAX_OCTETS,
  tailleDeVueHtml,
} from "./vue-html.js";
import { schemaDeFamille } from "./familles.js";

/**
 * LA FICHE PERSONNALISEE D'UNE PORTE (chantier `fiche-canva-v2`,
 * `docs/socle/fiche-suivi.md` section 3 et `docs/socle/vues-html.md`).
 *
 * Une porte qui l'active (`porte.fichePerso`) offre une vue de fiche de plus,
 * « Fiche personnalisée » : une vue HTML ORDINAIRE de `ecran.vuesHtml`, mêmes
 * schéma, cadre fermé et outils MCP, dont l'id est FIXE (`idDeFichePerso`) et
 * qui n'est offerte que par SA porte (`vuesDeFicheDeLaPorte`, `app-screens.ts`).
 *
 * Ce fichier porte aussi, sans React, tout ce que l'ATELIER (la vue sans HTML
 * encore) montre : le protocole et la page d'exemple que le serveur RECOPIE
 * (`functions/src/ia/vues-html.ts`, un test de parité les lit), les champs de
 * la porte (le MIROIR client de `champsDuJeu` du serveur), le prompt à
 * copier, le bloc à coller à un agent MCP, et la lecture locale d'une page
 * déposée avant publication.
 */

/** Le préfixe de l'id d'une fiche personnalisée : `html.perso-<porte>`. */
export const VUE_FICHE_PERSO_PREFIXE = `${VUE_HTML_PREFIXE}perso-`;
/** Le libellé de la vue dans le sélecteur des vues de fiche. */
export const FICHE_PERSO_LIBELLE = "Fiche personnalisée";

/**
 * L'id de la fiche personnalisée d'une porte : `html.perso-<porte>`. Le slug
 * après `html.` tient en 59 caractères au plus, tirets finaux retirés, donc
 * l'id est toujours conforme à `VUE_HTML_ID_RE` (un id de porte très long est
 * tronqué).
 */
export function idDeFichePerso(porteId: string): string {
  const slug = `${VUE_FICHE_PERSO_PREFIXE.slice(VUE_HTML_PREFIXE.length)}${porteId}`.slice(0, 59).replace(/-+$/g, "");
  return `${VUE_HTML_PREFIXE}${slug}`;
}

/** Cet id est-il celui d'une fiche personnalisée (de n'importe quelle porte) ? */
export function estIdDeFichePerso(id: string): boolean {
  return id.startsWith(VUE_FICHE_PERSO_PREFIXE);
}

/* -------------------------------------------------------------------------- */
/* Le protocole et l'exemple que le serveur RECOPIE                           */
/* -------------------------------------------------------------------------- */

/**
 * Le protocole v1 du cadre, tel que `VueHtmlIsolee` (@taki/ui) le parle. Le
 * prompt et `vue_contrat` le servent tel quel, une clef par puce : les limites
 * (taille, réseau) et les couleurs à employer n'y sont PAS, elles ont leur
 * section.
 */
export const PROTOCOLE_VUE_HTML_V1 = {
  version: VUE_HTML_PROTOCOLE,
  cadre: 'iframe sandbox="allow-scripts", origine opaque (ni cookie, ni stockage). Document complet, scripts en ligne exécutés. Politique de contenu : default-src \'none\'; script-src \'unsafe-inline\'; style-src \'unsafe-inline\'; img-src data: blob:; font-src data:; connect-src \'none\'; form-action \'none\'; base-uri \'none\'.',
  recu: 'Message { taki: "vue-html", version: 1, type: "donnees", donnees, theme } reçu par window.addEventListener("message", e => …), au chargement puis à chaque changement de la fiche.',
  donnees: 'donnees = { fiche: { id, family, type?, champs: { "<name>": valeur }, libelles: { "<name>": "<texte>" } }, liees: { "<id de lien>": [ { id, titre } ] } }. champs : les name déclarés seulement (taki-champs ou fields) ; valeur JSON (texte, nombre, booléen, null si vide), instant en ISO 8601, date pure en AAAA-MM-JJ, liste multiple en tableau. libelles : champ à choix (libellé de l’option), champ qui désigne une fiche (son nom), champ de type lecture (valeur affichée) ; afficher libelles[name] s’il existe, sinon champs[name]. liees : une clé par lien déclaré (taki-liens ou links), 200 fiches au plus, archivées exclues.',
  theme: 'theme = { "--accent", "--accent-label", "--accent-tint", "--accent-ink", "--ink-900", "--ink-500", "--surface", "--border", "--font-sans" } : valeurs CSS de l’org.',
  envoye: 'Facultatif : parent.postMessage({ taki: "vue-html", type: "pret" }, "*") quand l’écoute est prête, le cadre renvoie les données. Tout autre message est ignoré.',
  lectureSeule: true,
};

/**
 * LES ESPACEMENTS D'UNE VUE DE FICHE, en pixels : ce que la page d'une fiche
 * personnalisee doit suivre pour s'asseoir DANS la vue comme la Fiche native.
 * Le socle n'a AUCUNE variable CSS d'espacement (seulement des rayons) : ce sont
 * des VALEURS, lues dans le code. `margeCotePx` et `margeHautPx` : le `px-5 pt-6`
 * de `packages/ui/src/views/core/RecordView.tsx` ; `ecartPx` et `ecartMaxPx` :
 * son `gap-4` et son `gap-y-9` ; `rayonPx` et `rayonGrandPx` : `--radius-md` et
 * `--radius-lg` de `packages/ui/src/styles/tokens.css`. `margeMaxPx` est le
 * PLAFOND de la marge exterieure de la page. Le prompt et les deux pages
 * d'exemple les lisent ici, le serveur les recopie dans sa page d'exemple.
 */
export const MISE_EN_PAGE_FICHE_PERSO = {
  margeCotePx: 20,
  margeHautPx: 24,
  margeMaxPx: 24,
  ecartPx: 16,
  ecartMaxPx: 36,
  rayonPx: 10,
  rayonGrandPx: 14,
} as const;

/** Les couleurs de la page : celles de l'environnement (le thème reçu) ou les siennes (la charte de la personne). */
export type CouleursDeFichePerso = "environnement" | "propres";
/** Absent = l'environnement. */
export const COULEURS_DE_FICHE_PERSO_DEFAUT: CouleursDeFichePerso = "environnement";

/** Lit une valeur de préférence : tout ce qui n'est pas « propres » est l'environnement. */
export function couleursDeFichePerso(valeur: unknown): CouleursDeFichePerso {
  return valeur === "propres" ? "propres" : COULEURS_DE_FICHE_PERSO_DEFAUT;
}

/** La hauteur de la page : elle peut dépasser la vue (la vue défile) ou tenir entière dans la hauteur de la vue. */
export type HauteurDeFichePerso = "defilement" | "fixe";
/** Absent = le défilement. */
export const HAUTEUR_DE_FICHE_PERSO_DEFAUT: HauteurDeFichePerso = "defilement";

/** Lit une valeur de préférence : tout ce qui n'est pas « fixe » est le défilement. */
export function hauteurDeFichePerso(valeur: unknown): HauteurDeFichePerso {
  return valeur === "fixe" ? "fixe" : HAUTEUR_DE_FICHE_PERSO_DEFAUT;
}

const MEP = MISE_EN_PAGE_FICHE_PERSO;

/**
 * LA PAGE D'EXEMPLE, en deux variantes de la MEME structure : la fiche, un petit
 * graphique SVG des champs numeriques. Celle de l'environnement pose les
 * couleurs recues et les utilise ; celle des couleurs propres ne pose que la
 * police et ecrit ses couleurs en dur dans des variables locales.
 */
function exempleDeVue(propres: boolean): string {
  const couleurs = propres
    ? `  /* Remplacer par les couleurs de la charte. */
  :root { --couleur-principale: #1f2937; --couleur-secondaire: #6b7280; --couleur-accent: #7c3aed; --couleur-fond: #ffffff; }
  body { margin: 0; padding: ${MEP.margeHautPx}px ${MEP.margeCotePx}px; font-family: var(--font-sans, sans-serif); color: var(--couleur-principale); background: var(--couleur-fond); }`
    : `  body { margin: 0; padding: ${MEP.margeHautPx}px ${MEP.margeCotePx}px; font-family: var(--font-sans, sans-serif); color: var(--ink-900, #111); background: var(--surface, #fff); }`;
  const [secondaire, accent] = propres
    ? ["var(--couleur-secondaire)", "var(--couleur-accent)"]
    : ["var(--ink-500, #666)", "var(--accent, #7c3aed)"];
  const theme = propres
    ? `    if (theme && theme['--font-sans']) document.documentElement.style.setProperty('--font-sans', theme['--font-sans']);`
    : `    for (const [nom, valeur] of Object.entries(theme || {})) document.documentElement.style.setProperty(nom, valeur);`;
  return `<!doctype html>
<html lang="fr"><head>
<meta name="taki-champs" content="socle:name">
<meta charset="utf-8">
<style>
${couleurs}
  h1 { font-size: 20px; margin: 0 0 ${MEP.ecartPx}px; }
  dl { display: grid; grid-template-columns: max-content 1fr; gap: 6px ${MEP.ecartPx}px; margin: 0 0 ${MEP.ecartPx}px; }
  dt { color: ${secondaire}; } dd { margin: 0; }
  .barre { fill: ${accent}; } .etiquette { font-size: 12px; fill: ${secondaire}; }
</style></head>
<body>
<h1 id="titre">Chargement…</h1>
<dl id="champs"></dl>
<svg id="graphique" width="100%" height="160" role="img" aria-label="Valeurs numériques de la fiche"></svg>
<ul id="liees"></ul>
<script>
  const texte = (v) => v === null || v === undefined ? '' : Array.isArray(v) ? v.join(', ') : String(v);
  function dessiner({ donnees, theme }) {
${theme}
    const fiche = donnees.fiche;
    document.getElementById('titre').textContent = texte(fiche.champs['socle:name']) || fiche.id;
    const dl = document.getElementById('champs'); dl.replaceChildren();
    for (const [nom, valeur] of Object.entries(fiche.champs)) {
      const dt = document.createElement('dt'); dt.textContent = nom;
      const dd = document.createElement('dd'); dd.textContent = fiche.libelles?.[nom] ?? texte(valeur);
      dl.append(dt, dd);
    }
    const nombres = Object.entries(fiche.champs).filter(([, v]) => typeof v === 'number');
    const max = Math.max(1, ...nombres.map(([, v]) => Math.abs(v)));
    const svg = document.getElementById('graphique'); svg.replaceChildren();
    nombres.forEach(([nom, v], i) => {
      const y = 10 + i * 36, largeur = Math.round((Math.abs(v) / max) * 240);
      svg.insertAdjacentHTML('beforeend', '<rect class="barre" x="120" y="' + y + '" width="' + largeur + '" height="20" rx="4"></rect>'
        + '<text class="etiquette" x="0" y="' + (y + 15) + '"></text>');
      svg.lastElementChild.textContent = nom + ' : ' + v;
    });
    const ul = document.getElementById('liees'); ul.replaceChildren();
    for (const [lien, fiches] of Object.entries(donnees.liees || {})) for (const liee of fiches) {
      const li = document.createElement('li'); li.textContent = lien + ' : ' + liee.titre; ul.append(li);
    }
  }
  window.addEventListener('message', (e) => {
    if (e.data && e.data.taki === 'vue-html' && e.data.version === 1 && e.data.type === 'donnees') dessiner(e.data);
  });
  parent.postMessage({ taki: 'vue-html', type: 'pret' }, '*');
</script>
</body></html>`;
}

/** L'exemple complet : la fiche, un petit graphique SVG des champs numeriques, les couleurs recues. */
export const EXEMPLE_DE_VUE_HTML = exempleDeVue(false);

/** La même page, sans le thème : ses couleurs sont en dur, dans des variables locales à remplacer par celles de la charte. */
export const EXEMPLE_DE_VUE_HTML_COULEURS_PROPRES = exempleDeVue(true);

/* -------------------------------------------------------------------------- */
/* Les champs de la porte : le miroir client de `champsDuJeu` du serveur      */
/* -------------------------------------------------------------------------- */

/** Un champ que la page peut lire, dans le vocabulaire des colonnes (`socle:status`, ou la clef nue d'un champ déclaré). */
export interface ChampDeFichePerso {
  name: string;
  label: string;
  type: string;
  options?: { value: string; label: string }[];
}

type OptionLue = { value: string; label?: string | undefined; archived?: boolean | undefined };
type ColonneLue = { champ: string; label?: string | undefined; type?: string | undefined; options?: readonly OptionLue[] | undefined };
type JeuLu = { family: string; type?: string | undefined };

function memeJeuLu(a: JeuLu, b: JeuLu): boolean {
  return a.family === b.family && (a.type ?? null) === (b.type ?? null);
}

/**
 * LES CHAMPS QUE LA PAGE D'UNE PORTE PEUT LIRE, comme le serveur les donne à
 * `vue_contrat` (`champsDuJeu`, `functions/src/ia/vues-html.ts`) : les natifs
 * `socle:*` des colonnes de la porte puis de l'écran (la porte d'abord), puis
 * les champs déclarés du jeu SANS ceux qu'il masque (`jeux[].masques`), les
 * options archivées écartées. Une porte inconnue ou sans jeu n'en a aucun.
 * `jeux` est la liste `jeux` de `taki.fields.json` (ou de la déclaration de
 * l'org) ; un test prouve le même résultat que le serveur.
 */
export function champsDeFichePerso(
  ecran: {
    portes: readonly { id: string; jeu?: JeuLu | undefined; colonnes?: readonly unknown[] | undefined }[];
    colonnes?: readonly ColonneLue[] | undefined;
  },
  porteId: string,
  jeux: readonly {
    family: string;
    type?: string | undefined;
    champs: readonly { key: string; label: string; type: string; options?: readonly OptionLue[] | undefined }[];
    masques?: readonly string[] | undefined;
  }[],
): ChampDeFichePerso[] {
  const porte = ecran.portes.find((candidate) => candidate.id === porteId);
  if (porte?.jeu === undefined) return [];
  const jeu = porte.jeu;
  const options = (liste: readonly OptionLue[] | undefined): { value: string; label: string }[] =>
    (liste ?? []).filter((option) => option.archived !== true).map((option) => ({ value: option.value, label: option.label ?? option.value }));
  /* LES NATIFS DE LA FAMILLE DU JEU SEULEMENT : les colonnes de l'ecran servent
     toutes ses portes (`socle:budgetHours` des projets a cote des taches), et
     `vue_proposer` refuse un natif que la famille n'a pas. */
  let famille: Set<string>;
  try {
    famille = new Set(Object.keys(schemaDeFamille(jeu.family).shape));
  } catch {
    famille = new Set();
  }
  const natives = new Map<string, ColonneLue>();
  for (const source of [porte.colonnes, ecran.colonnes]) {
    for (const brute of source ?? []) {
      const colonne = brute as ColonneLue | null;
      if (colonne !== null && typeof colonne === "object" && typeof colonne.champ === "string" && colonne.champ.startsWith("socle:") && famille.has(colonne.champ.slice(6)) && !natives.has(colonne.champ)) {
        natives.set(colonne.champ, colonne);
      }
    }
  }
  const natifs = [...natives.values()].map((colonne): ChampDeFichePerso => ({
    name: colonne.champ,
    label: colonne.label ?? colonne.champ.slice(6),
    type: colonne.type ?? "text",
    ...(colonne.options !== undefined && colonne.options.length > 0 ? { options: options(colonne.options) } : {}),
  }));
  const definition = jeux.find((candidat) => memeJeuLu(candidat, jeu));
  const masques = new Set(definition?.masques ?? []);
  const declares = (definition?.champs ?? [])
    .filter((champ) => !masques.has(champ.key))
    .map((champ): ChampDeFichePerso => ({
      name: champ.key,
      label: champ.label,
      type: champ.type,
      ...(champ.options !== undefined && champ.options.length > 0 ? { options: options(champ.options) } : {}),
    }));
  return [...natifs, ...declares];
}

/* -------------------------------------------------------------------------- */
/* Le prompt à copier                                                         */
/* -------------------------------------------------------------------------- */

export interface EntreeDePromptDeFichePerso {
  /** Le libellé de la porte ; sert de nom de fiche quand `ficheSingulier` manque. */
  porteLabel: string;
  /** Le nom d'UNE fiche de ce jeu (« Tâche de projet ») ; absent : le libellé de la porte. */
  ficheSingulier?: string | undefined;
  champs: readonly ChampDeFichePerso[];
  liens: readonly { id: string; label: string }[];
  /** Les couleurs de la page ; absent : celles de l'environnement. */
  couleurs?: CouleursDeFichePerso | undefined;
  /** La hauteur de la page ; absent : le défilement. */
  hauteur?: HauteurDeFichePerso | undefined;
}


/**
 * La section « Mise en page » du prompt, la même pour les deux choix de
 * couleurs. Exportée : `vue_contrat` la sert telle quelle (le serveur la
 * recopie, la parité la tient).
 */
export const CONSIGNE_MISE_EN_PAGE: string = [
  "## Mise en page",
  "Pleine largeur de la vue, sous la barre de la fiche : pas de cadre, d'ombre, de fond de carte ni de largeur maximale centrée.",
  `Marge extérieure ${MEP.margeMaxPx} px au plus de chaque côté : body { margin: 0; padding: ${MEP.margeHautPx}px ${MEP.margeCotePx}px; }. Entre deux blocs ${MEP.ecartPx} px, ${MEP.ecartMaxPx} px au plus. Rayons ${MEP.rayonPx} px et ${MEP.rayonGrandPx} px.`,
].join("\n");

/**
 * La section « Couleurs » du prompt quand la personne garde les siennes.
 * Exportée : `vue_contrat` la sert telle quelle en « Mes propres couleurs ».
 */
export const CONSIGNE_COULEURS_PROPRES: string = [
  "## Couleurs",
  "Utilise UNIQUEMENT les couleurs de la référence, de la maquette ou de la charte fournie avec ce prompt ; sans elles, demande-les avant d'écrire la page.",
  `N'applique JAMAIS les variables de couleur de theme : écris tes couleurs dans la page. Seule --font-sans peut servir.`,
].join("\n");

/**
 * La consigne de couleurs en « Couleurs de l'environnement » (sans titre : le
 * prompt pose « ## Couleurs » au-dessus). Exportée : `vue_contrat` la sert telle quelle.
 */
export const CONSIGNE_COULEURS_ENVIRONNEMENT: string =
  "Pose les variables de theme reçues par document.documentElement.style.setProperty(nom, valeur) et utilise-les : var(--accent), var(--ink-900), var(--ink-500), var(--surface), var(--border), var(--font-sans). Jamais de couleur en dur.";

/** La consigne de hauteur « Défilement » (sans titre). Exportée : `vue_contrat` la sert telle quelle. */
export const CONSIGNE_HAUTEUR_DEFILEMENT: string = "La page peut être plus haute que la vue : la vue défile.";

/** La consigne de hauteur « Fixe » (sans titre). Exportée : `vue_contrat` la sert telle quelle. */
export const CONSIGNE_HAUTEUR_FIXE: string = [
  "La page tient ENTIÈRE dans la hauteur du cadre, quelle qu'elle soit, sans aucune barre de défilement. Le cadre fait la hauteur de la vue : 100vh y vaut la hauteur de la vue.",
  "- html, body { height: 100%; margin: 0; overflow: hidden; } avec le padding de la mise en page, box-sizing: border-box.",
  "- Mise en page en flex ou grid qui remplit et se RESSERRE : tailles relatives (%, fr, min(), clamp() avec des unités vh).",
  "- Aucune taille d'écran ni hauteur de bloc en px fixe. Aucun texte qui déborde : texte qui se réduit, line-clamp en dernier recours.",
].join("\n");

/** La consigne de hauteur d'un choix. */
export function consigneDeHauteur(hauteur: HauteurDeFichePerso): string {
  return hauteur === "fixe" ? CONSIGNE_HAUTEUR_FIXE : CONSIGNE_HAUTEUR_DEFILEMENT;
}

/** La ligne « contenu seulement » : ce que la page ne montre pas autour de la fiche. `titreGenerique` : le nom du type de fiche. */
function consigneContenuSeul(titreGenerique: string): string {
  return `La page montre le contenu de la fiche seulement : ni nom d'app ni de porte, ni fil d'Ariane, ni titre générique (« ${titreGenerique} »), ni mention « lecture seule ».`;
}

/**
 * LE PROMPT QU'UNE PERSONNE COLLE DANS SON IA (Claude, ChatGPT...) pour
 * qu'elle écrive la page de SA fiche. Fabriqué des vrais champs de la porte :
 * l'IA n'invente rien, et sa page se déclare (`<meta name="taki-champs">`),
 * ce que `lirePageDeFichePerso` vérifie avant publication. Court et factuel,
 * sans phrase de contexte ni exemple (la page d'exemple reste servie par
 * `vue_contrat`). Sections, dans l'ordre : Champs (et liens), Protocole,
 * Limites, Livrable, Couleurs, Mise en page, Hauteur.
 */
export function promptDeFichePerso(entree: EntreeDePromptDeFichePerso): string {
  const fiche = entree.ficheSingulier ?? entree.porteLabel;
  const propres = couleursDeFichePerso(entree.couleurs) === "propres";
  const lignesDeChamps = entree.champs.map((champ) => {
    const choix = champ.options !== undefined && champ.options.length > 0
      ? ` | choix : ${champ.options.map((option) => `${option.value} = ${option.label}`).join(", ")}`
      : "";
    return `- ${champ.name} | ${champ.label} | ${champ.type}${choix}`;
  });
  const parties: string[] = [];
  parties.push(
    [
      "## Champs",
      "name | libellé | type | choix. name à recopier tel quel ; aucun autre champ.",
      ...(lignesDeChamps.length > 0 ? lignesDeChamps : ["(aucun champ)"]),
      ...(entree.liens.length > 0
        ? ["Liens (donnees.liees[id] : liste de { id, titre }) :", ...entree.liens.map((lien) => `- ${lien.id} | ${lien.label}`)]
        : []),
    ].join("\n"),
  );
  const protocole = PROTOCOLE_VUE_HTML_V1;
  parties.push(
    [
      "## Protocole",
      `- cadre : ${protocole.cadre}`,
      `- réception : ${protocole.recu}`,
      `- données : ${protocole.donnees}`,
      `- thème : ${protocole.theme}`,
      `- envoi : ${protocole.envoye}`,
    ].join("\n"),
  );
  parties.push(
    [
      "## Limites",
      `- ${Math.round(VUE_HTML_TAILLE_MAX_OCTETS / 1024)} Ko au plus.`,
      "- Aucun réseau : ni fetch, ni CDN, ni police ou image distante.",
      "- Scripts et styles en ligne.",
    ].join("\n"),
  );
  parties.push(
    [
      "## Livrable",
      "Un seul fichier .html, la page complète, rien d'autre.",
      'OBLIGATOIRE, en tout début de <head> : <meta name="taki-champs" content="nom1,nom2"> avec les name EXACTS des champs lus. Un name absent de « Champs » fait refuser la page.',
      ...(entree.liens.length > 0 ? ['Fiches liées lues : <meta name="taki-liens" content="id1"> avec les id des liens utilisés.'] : []),
      consigneContenuSeul(fiche),
    ].join("\n"),
  );
  parties.push(propres ? CONSIGNE_COULEURS_PROPRES : `## Couleurs\n${CONSIGNE_COULEURS_ENVIRONNEMENT}`);
  parties.push(CONSIGNE_MISE_EN_PAGE);
  parties.push(`## Hauteur\n${consigneDeHauteur(hauteurDeFichePerso(entree.hauteur))}`);
  return parties.join("\n\n");
}

/* -------------------------------------------------------------------------- */
/* Le bloc à coller à un agent MCP                                            */
/* -------------------------------------------------------------------------- */

export interface EntreeDeContexteMcp {
  orgId: string;
  appId: string;
  door: string;
  porteLabel: string;
  /** Le nom d'UNE fiche de ce jeu (« Tâche de projet ») ; absent : le libellé de la porte. */
  ficheSingulier?: string | undefined;
  recordId: string;
  viewId: string;
  label: string;
  champs: readonly string[];
  liens: readonly string[];
  /** Les couleurs de la page ; absent : celles de l'environnement. */
  couleurs?: CouleursDeFichePerso | undefined;
  /** La hauteur de la page ; absent : le défilement. */
  hauteur?: HauteurDeFichePerso | undefined;
}

/**
 * LE BLOC À COLLER à un agent déjà connecté au MCP de MyTaki (Claude Code,
 * Codex...) : où déposer, avec quel `viewId`, et les trois appels dans leur
 * ordre (`vue_contrat`, `vue_proposer`, `vue_publier`). Les objets d'appel
 * sont du JSON réel ; la publication n'a lieu que sur l'accord de la personne.
 * Le détail des couleurs, de la mise en page et de la hauteur vient de
 * `vue_contrat`, appelé avec le choix de la personne.
 */
export function contexteMcpDeFichePerso(entree: EntreeDeContexteMcp): string {
  const appel = (outil: string, objet: Record<string, unknown>): string => `${outil}(${JSON.stringify(objet)})`;
  const couleurs = couleursDeFichePerso(entree.couleurs);
  const hauteur = hauteurDeFichePerso(entree.hauteur);
  const proposition: Record<string, unknown> = {
    appId: entree.appId,
    door: entree.door,
    label: entree.label,
    fields: [...entree.champs],
    ...(entree.liens.length > 0 ? { links: [...entree.liens] } : {}),
    html: "<la page HTML complète>",
    recordId: entree.recordId,
    viewId: entree.viewId,
  };
  return [
    `Dépose la fiche personnalisée de la porte « ${entree.porteLabel} » dans MyTaki.`,
    "",
    "Où déposer",
    `- Organisation : ${entree.orgId}`,
    `- App : ${entree.appId}`,
    `- Porte : ${entree.door} (${entree.porteLabel})`,
    `- Fiche d'aperçu : ${entree.recordId}`,
    `- viewId : ${entree.viewId}`,
    "",
    "Dépose uniquement ici, avec ce viewId. N'appelle aucun autre outil d'écriture.",
    "",
    consigneContenuSeul(entree.ficheSingulier ?? entree.porteLabel),
    `Mise en page : pleine largeur de la vue, sans cadre, ombre ni largeur maximale centrée ; marge extérieure ${MEP.margeMaxPx} px au plus (body { margin: 0; padding: ${MEP.margeHautPx}px ${MEP.margeCotePx}px; }), ${MEP.ecartPx} px entre deux blocs, rayons ${MEP.rayonPx} px et ${MEP.rayonGrandPx} px.`,
    ...(couleurs === "propres"
      ? ["Couleurs : uniquement celles de la référence ou de la charte de la personne (demande-les-lui sinon) ; n'applique pas les variables de couleur de theme."]
      : []),
    ...(hauteur === "fixe"
      ? ["Hauteur : la page tient entière dans la hauteur de la vue, sans barre de défilement (le détail est dans vue_contrat, height)."]
      : []),
    "",
    "Les appels, dans l'ordre",
    `1. ${appel("vue_contrat", { appId: entree.appId, door: entree.door, viewId: entree.viewId, couleurs, hauteur })} : champs, liens, protocole, limites, couleurs, mise en page, hauteur et page d'exemple (et la page déjà déposée, s'il y en a une).`,
    `2. ${appel("vue_proposer", proposition)} : écris la page, déclare ses champs dans fields.`,
    "3. Montre previewUrl à la personne : l'aperçu s'ouvre sur sa fiche.",
    `4. ${appel("vue_publier", { draftId: "<draftId rendu par vue_proposer>" })} : seulement si la personne est d'accord.`,
  ].join("\n");
}

/* -------------------------------------------------------------------------- */
/* La lecture locale d'une page déposée                                       */
/* -------------------------------------------------------------------------- */

export type LectureDePageDeFichePerso =
  | { ok: true; champs: string[]; liens: string[]; taille: number; detectes: boolean }
  | { ok: false; erreurs: string[]; taille: number };

function metaTaki(html: string, nom: string): string | null {
  const balises = html.match(/<meta\b[^>]*>/gi) ?? [];
  for (const balise of balises) {
    const designe = new RegExp(`\\bname\\s*=\\s*(?:"${nom}"|'${nom}')`, "i").test(balise);
    if (!designe) continue;
    const contenu = /\bcontent\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(balise);
    return contenu === null ? "" : (contenu[1] ?? contenu[2] ?? "");
  }
  return null;
}

function listeDeMeta(valeur: string): string[] {
  return [...new Set(valeur.split(/[,\s]+/).filter((nom) => nom !== ""))];
}

function echapper(texte: string): string {
  return texte.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * LIT UNE PAGE DÉPOSÉE avant de l'envoyer au serveur : non vide, 128 Ko au
 * plus (octets UTF-8), les champs qu'elle lit (déclarés dans
 * `<meta name="taki-champs" content="a,b">`, ou, à défaut, détectés : un nom
 * connu entre guillemets ou après `champs.`), tous connus de la porte, et les
 * liens (`<meta name="taki-liens">`) tous déclarés par elle. Les messages sont
 * courts et se corrigent. Le serveur refait ces contrôles à la publication.
 */
export function lirePageDeFichePerso(
  html: string,
  connus: { champs: readonly string[]; liens: readonly string[] },
): LectureDePageDeFichePerso {
  const taille = tailleDeVueHtml(html);
  if (html.trim() === "") return { ok: false, erreurs: ["La page est vide."], taille };
  const erreurs: string[] = [];
  if (taille > VUE_HTML_TAILLE_MAX_OCTETS) {
    erreurs.push(`La page pèse ${Math.ceil(taille / 1024)} Ko, ${Math.round(VUE_HTML_TAILLE_MAX_OCTETS / 1024)} Ko au plus : retire les bibliothèques collées, dessine en SVG.`);
  }
  const metaChamps = metaTaki(html, "taki-champs");
  let champs: string[];
  let detectes = false;
  if (metaChamps !== null) {
    champs = listeDeMeta(metaChamps);
  } else {
    detectes = true;
    champs = connus.champs.filter((nom) => {
      const n = echapper(nom);
      return new RegExp(`["'\`]${n}["'\`]`).test(html) || new RegExp(`champs\\s*\\??\\.\\s*${n}(?![A-Za-z0-9_])`).test(html);
    });
  }
  if (champs.length === 0) {
    erreurs.push('Aucun champ lu : ajoute <meta name="taki-champs" content="nom1,nom2"> en tête du <head>, avec les name des champs que la page lit.');
  } else {
    const inconnus = champs.filter((nom) => !connus.champs.includes(nom));
    if (inconnus.length > 0) {
      erreurs.push(`${inconnus.length === 1 ? "Champ inconnu" : "Champs inconnus"} : ${inconnus.slice(0, 10).join(", ")}. Les noms autorisés figurent dans « Voir le prompt ».`);
    }
    if (champs.length > VUE_HTML_CHAMPS_MAX) erreurs.push(`${champs.length} champs lus, ${VUE_HTML_CHAMPS_MAX} au plus.`);
  }
  const metaLiens = metaTaki(html, "taki-liens");
  const liens = metaLiens === null ? [] : listeDeMeta(metaLiens);
  const liensInconnus = liens.filter((id) => !connus.liens.includes(id));
  if (liensInconnus.length > 0) {
    erreurs.push(`${liensInconnus.length === 1 ? "Lien inconnu" : "Liens inconnus"} : ${liensInconnus.join(", ")}. Les liens autorisés figurent dans « Voir le prompt ».`);
  }
  if (liens.length > VUE_HTML_LIENS_MAX) erreurs.push(`${liens.length} liens lus, ${VUE_HTML_LIENS_MAX} au plus.`);
  if (erreurs.length > 0) return { ok: false, erreurs, taille };
  return { ok: true, champs, liens, taille, detectes };
}
